import { useMemo, useState } from 'react';

import { CarpoolTripData } from '../../../../graphql/query/carpoolTripsQuery.graphql';
import { useDeleteCarpoolTripMutation } from '../../../../hooks/useDeleteCarpoolTripMutation';
import { useJoinCarpoolTripMutation } from '../../../../hooks/useJoinCarpoolTripMutation';
import { useLeaveCarpoolTripMutation } from '../../../../hooks/useLeaveCarpoolTripMutation';
import { useStaffDeleteCarpoolTripMutation } from '../../../../hooks/useStaffDeleteCarpoolTripMutation';
import { useStaffJoinCarpoolTripMutation } from '../../../../hooks/useStaffJoinCarpoolTripMutation';
import { useStaffLeaveCarpoolTripMutation } from '../../../../hooks/useStaffLeaveCarpoolTripMutation';

interface UseCarpoolTripDrawerProps {
  currentUserId: string | null;
  hasPaidTicket: boolean;
  isStaff: boolean;
  trip: CarpoolTripData;
  userJoinedTripId: string | null;
  onClose: () => void;
}

export const useCarpoolTripDrawer = ({
  currentUserId,
  hasPaidTicket,
  isStaff,
  trip,
  userJoinedTripId,
  onClose,
}: UseCarpoolTripDrawerProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEventUsersDrawerOpen, setIsEventUsersDrawerOpen] = useState(false);
  const { joinCarpoolTrip } = useJoinCarpoolTripMutation();
  const { leaveCarpoolTrip } = useLeaveCarpoolTripMutation();
  const { deleteCarpoolTrip } = useDeleteCarpoolTripMutation();
  const { staffJoinCarpoolTrip } = useStaffJoinCarpoolTripMutation();
  const { staffLeaveCarpoolTrip } = useStaffLeaveCarpoolTripMutation();
  const { staffDeleteCarpoolTrip } = useStaffDeleteCarpoolTripMutation();

  const isCurrentUserDriver = currentUserId === trip.driver.id;
  const isCurrentUserPassenger = trip.passengers.some(
    (passenger) => passenger.id === currentUserId,
  );
  const isUserAlreadyInAnotherTrip =
    userJoinedTripId !== null && userJoinedTripId !== trip.id;
  const canJoinTrip =
    hasPaidTicket &&
    !isCurrentUserDriver &&
    !isCurrentUserPassenger &&
    !isUserAlreadyInAnotherTrip &&
    trip.remainingSeats > 0;
  const canDeleteTrip = isCurrentUserDriver || isStaff;

  const excludedUserIds = useMemo(
    () => [trip.driver.id, ...trip.passengers.map((passenger) => passenger.id)],
    [trip.driver.id, trip.passengers],
  );

  const onJoinTrip = async () => {
    await joinCarpoolTrip(trip.id);
    onClose();
  };

  const onLeaveTrip = async () => {
    await leaveCarpoolTrip(trip.id);
    onClose();
  };

  const onShowDeleteDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const onCloseDeleteDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const onDeleteTrip = async () => {
    if (isStaff && !isCurrentUserDriver) {
      await staffDeleteCarpoolTrip(trip.id);
    } else {
      await deleteCarpoolTrip(trip.id);
    }

    onCloseDeleteDialog();
    onClose();
  };

  const onRemovePassenger = async (userId: string) => {
    await staffLeaveCarpoolTrip(trip.id, userId);
  };

  const onOpenEventUsersDrawer = () => {
    setIsEventUsersDrawerOpen(true);
  };

  const onCloseEventUsersDrawer = () => {
    setIsEventUsersDrawerOpen(false);
  };

  const onAddPassenger = async (userId: string) => {
    await staffJoinCarpoolTrip(trip.id, userId);
    onCloseEventUsersDrawer();
  };

  return {
    canDeleteTrip,
    canJoinTrip,
    excludedUserIds,
    isCurrentUserDriver,
    isCurrentUserPassenger,
    isDeleteDialogOpen,
    isEventUsersDrawerOpen,
    isStaff,
    isUserAlreadyInAnotherTrip,
    onAddPassenger,
    onCloseDeleteDialog,
    onCloseEventUsersDrawer,
    onDeleteTrip,
    onJoinTrip,
    onLeaveTrip,
    onOpenEventUsersDrawer,
    onRemovePassenger,
    onShowDeleteDialog,
  };
};
